import React from 'react';
import { StyleSheet } from 'react-native';
import PropTypes from 'prop-types';
import { useRouter } from 'expo-router';
import Text from '@/components/Typography/Text';
import ChatInputBanner from '@/components/ChatInput/ChatInputBanner';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import { useChatInputStore } from '@/store';
import useResponsiveness from '@/hooks/useResponsiveness';
import useTranslation from '@/hooks/useTranslation';

const styles = StyleSheet.create({
  message: {
    lineHeight: 19,
    flexShrink: 1,
  },
  link: {
    display: 'inline',
    textDecorationLine: 'underline',
    marginHorizontal: 1,
  },
});

function ChatInputConversationContextBanner({ level, isDetached = false }) {
  const router = useRouter();
  const { theme } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const dismissBanner = useChatInputStore(state => state.dismissBanner);

  const ctaTitles = React.useMemo(() => t('chatInput.conversationContext.ctaTitles'), [t]);
  const mobileCta = React.useMemo(() => t('chatInput.conversationContext.mobileCta'), [t]);

  const titleRef = React.useRef(null);
  if (!titleRef.current) {
    if (isXS) {
      titleRef.current = mobileCta;
    } else {
      const idx = Math.floor(Math.random() * ctaTitles.length);
      titleRef.current = ctaTitles[idx];
    }
  }
  const { pre, post } = titleRef.current;

  const goToNewChat = React.useCallback(() => {
    router.push('/app');
  }, [router]);

  const handleClose = React.useCallback(() => {
    try {
      dismissBanner('conversation-context-banner');
    } catch {}
  }, [dismissBanner]);

  return (
    <ChatInputBanner
      // Was "warning". This banner used to mean "you're running out of room
      // and will lose context"; it now means "FYI, we'll keep going by
      // summarizing older messages." That's neutral information, not a
      // warning, so it should not look alarmed.
      color="info"
      level={level}
      isCloseable={true}
      onClose={handleClose}
      isDetached={isDetached}
      useOverlayCloseButton={true}
    >
      <Text size="s" color={isDarkTheme(theme) ? 'white' : 'black'} style={styles.message}>
        {pre}
        <Text
          size="s"
          weight="medium"
          color={isDarkTheme(theme) ? 'white' : 'black'}
          style={styles.link}
          onPress={goToNewChat}
        >
          {t('chatInput.conversationContext.newChat')}
        </Text>
        {post}
      </Text>
    </ChatInputBanner>
  );
}

ChatInputConversationContextBanner.propTypes = {
  level: PropTypes.number,
  isDetached: PropTypes.bool,
};

export default React.memo(ChatInputConversationContextBanner);